import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  KNOWN_OPENAI_WIRE_APIS,
  type EgressConfig,
  type EgressProfileRecord,
  type EgressProfileUsage,
  type InternalProtocolSidecarCapabilities,
  type InternalProtocolSidecarStatusResponse,
  type OpenAIWireApi,
} from '@nadoutong/contracts';
import {
  useCreateEgressProfile,
  useDeleteEgressProfile,
  useEgressProfiles,
  useUpdateEgressProfile,
} from '../api/useEgressProfiles.ts';
import { useFirewallRulesets } from '../api/useFirewallRulesets.ts';
import { useProtocolSidecarStatus } from '../api/useProtocolSidecar.ts';
import {
  EgressConfigEditor,
  egressLaneLabel,
} from '../components/EgressConfigEditor.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { isOneOf } from '../lib/isOneOf.ts';
import {
  Button,
  buttonClass,
  Chip,
  ConfirmDialog,
  Dialog,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import {
  CirclePause,
  CirclePlay,
  FilePen,
  Fingerprint,
  Network,
  Package,
  Plus,
  Route,
  Save,
  Trash2,
  Webhook,
} from 'lucide-react';

type Translate = ReturnType<typeof useT>;

type StrategyAxis = {
  readonly label: string;
  readonly value: string;
};

function isChannelInstanceProfile(profile: EgressProfileRecord): boolean {
  return profile.id.startsWith('egp_global_');
}

function profileLaneLabel(profile: EgressProfileRecord, t: Translate): string {
  return profile.egress_config === null
    ? t('egress_profiles.strategy.legacy')
    : egressLaneLabel(profile.egress_config.lane);
}

/**
 * Strategy chips only surface axes that carry runtime meaning for the
 * configured lane — `matching` is consumed exclusively on the claude-code
 * lane, so showing it on api-direct or codex rows would be noise.
 */
function profileStrategyAxes(config: EgressConfig): readonly StrategyAxis[] {
  const axes: StrategyAxis[] = [
    { label: 'kind', value: config.kind },
    { label: 'mode', value: config.mode },
  ];
  if (config.target !== undefined) {
    axes.push({ label: 'target', value: config.target });
  }
  if (config.fixups !== undefined) {
    const fixups = [
      ...(config.fixups.fingerprint !== undefined ? [`fingerprint:${config.fixups.fingerprint}`] : []),
      ...(config.fixups.cch !== undefined ? [`cch:${config.fixups.cch}`] : []),
    ];
    axes.push({ label: 'fixups', value: fixups.join(' ') });
  }
  if (config.lane === 'claude-code') {
    axes.push({ label: 'matching', value: config.matching });
  }
  if (config.admission !== undefined) {
    axes.push({ label: 'admission', value: config.admission.mode });
  }
  if (config.adapter?.codex !== undefined) {
    axes.push({
      label: 'codex',
      value: `${config.adapter.codex.backend}/${config.adapter.codex.transport}`,
    });
  }
  // The images egress shape (#1886). Visible for the same reason it is opt-in:
  // arming it is only safe on an upstream whose native Images endpoint has been
  // MEASURED to honour `size` and `quality` (#1546 found one that accepts
  // `999x999` and generates anyway), and a per-upstream discipline that cannot
  // be read off the fleet is one nobody can enforce or audit.
  if (config.adapter?.openai?.images !== undefined) {
    axes.push({ label: 'openai', value: `images:${config.adapter.openai.images}` });
  }
  // The upstream adapter (#1872), for the same auditability reason: it changes
  // how this upstream's errors are classified and how its usage is metered,
  // and each member of the enum stands on a specific measurement. An operator
  // has to be able to see which upstreams are running on one.
  if (config.adapter?.openai?.vendor !== undefined) {
    axes.push({ label: 'openai', value: `vendor:${config.adapter.openai.vendor}` });
  }
  // The task lane's mounting (#1883 second half). Both seats are visible for
  // the auditability reason the openai vendor axis is: the vendor decides
  // which task grammar a dial speaks, and the coefficient scales what a
  // delivered generation bills — a calibration nobody can read off the fleet
  // is one nobody can audit. The coefficient renders even at its default so
  // "pass-through" is a stated fact rather than an absence.
  if (config.adapter?.arkVideo !== undefined) {
    axes.push({ label: 'ark_video_v1', value: `vendor:${config.adapter.arkVideo.vendor}` });
    axes.push({
      label: 'ark_video_v1',
      value: `coefficient:${String(config.adapter.arkVideo.deliveryQuantityCoefficient ?? 1)}`,
    });
  }
  if (config.adapter?.grokVideo !== undefined) {
    axes.push({ label: 'grok_video_v1', value: `vendor:${config.adapter.grokVideo.vendor}` });
    axes.push({
      label: 'grok_video_v1',
      value: `coefficient:${String(config.adapter.grokVideo.deliveryQuantityCoefficient ?? 1)}`,
    });
  }
  return axes;
}

function usageIsEmpty(usage: EgressProfileUsage): boolean {
  return usage.key_channel_bindings.length === 0 &&
    usage.sku_target_count === 0;
}

const USAGE_BINDING_PREVIEW_LIMIT = 3;

function UsageCell({ usage }: { readonly usage: EgressProfileUsage }): JSX.Element {
  const t = useT();
  if (usageIsEmpty(usage)) {
    return <span className="text-dense text-paper-faint">{t('egress_profiles.usage.none')}</span>;
  }
  const bindings = usage.key_channel_bindings;
  const shown = bindings.slice(0, USAGE_BINDING_PREVIEW_LIMIT);
  const overflow = bindings.length - shown.length;
  const secondary = [
    ...(usage.sku_target_count > 0
      ? [t('egress_profiles.usage.sku_targets', { count: usage.sku_target_count })]
      : []),
  ];
  return (
    <div className="flex flex-col gap-1">
      {bindings.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-dense text-paper-mid">
            {t('egress_profiles.usage.kcb_count', { count: bindings.length })}
          </span>
          {shown.map((binding) => (
            <Link
              key={`${binding.key_bucket_id}:${binding.channel_id}`}
              to={`/supply/upstreams/${encodeURIComponent(binding.upstream_id)}`}
              title={`${binding.upstream_name ?? binding.upstream_id} · ${binding.key_bucket_label}`}
              className="inline-flex items-center rounded bg-raised px-2 py-1 font-mono text-micro leading-none text-paper-mid transition-colors duration-120 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            >
              {binding.channel_slug}
            </Link>
          ))}
          {overflow > 0 ? (
            <span className="text-micro text-paper-low">+{overflow}</span>
          ) : null}
        </div>
      ) : null}
      {secondary.length > 0 ? (
        <span className="text-micro text-paper-low">{secondary.join(' · ')}</span>
      ) : null}
    </div>
  );
}

export function EgressProfilesPage(): JSX.Element {
  const t = useT();
  const profiles = useEgressProfiles();
  const protocolSidecar = useProtocolSidecarStatus();
  const capabilities: InternalProtocolSidecarCapabilities | null =
    protocolSidecar.data?.capabilities ?? null;
  const createProfile = useCreateEgressProfile();
  const updateProfile = useUpdateEgressProfile();
  const deleteProfile = useDeleteEgressProfile();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<EgressProfileRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EgressProfileRecord | null>(null);
  const [name, setName] = useState('');
  const [config, setConfig] = useState<EgressConfig | null>(null);
  const [wireApi, setWireApi] = useState<OpenAIWireApi>('both');
  const [editName, setEditName] = useState('');
  const [editConfig, setEditConfig] = useState<EgressConfig | null>(null);
  const [editWireApi, setEditWireApi] = useState<OpenAIWireApi>('both');

  // Degradation contract (epic protocol#473 R4): when the capabilities
  // publication is unreachable (or the sidecar is degraded) every config
  // write path is disabled — no defaults, no normalize-and-save. Reads
  // stay available; the editor shows the stored document read-only.
  const editingDisabled = capabilities === null;

  function openEdit(profile: EgressProfileRecord): void {
    setEditingProfile(profile);
    setEditName(profile.name);
    setEditConfig(profile.egress_config);
    setEditWireApi(profile.wire_api ?? 'both');
  }

  function handleCreate(): void {
    if (editingDisabled || config === null) {
      return;
    }
    createProfile.mutate({
      name: name.trim(),
      egress_config: config,
      enabled: true,
      wire_api: wireApi,
    }, {
      onSuccess: () => {
        setName('');
        setConfig(null);
        setWireApi('both');
        setCreateOpen(false);
        toast.success(t('egress_profiles.toast.created'));
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function handleEdit(): void {
    if (editingProfile === null || editingDisabled || editConfig === null) {
      return;
    }
    updateProfile.mutate({
      profile_id: editingProfile.id,
      name: editName.trim(),
      egress_config: editConfig,
      wire_api: editWireApi,
    }, {
      onSuccess: () => {
        setEditingProfile(null);
        toast.success(t('egress_profiles.toast.updated'));
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function toggle(profile: EgressProfileRecord): void {
    updateProfile.mutate({
      profile_id: profile.id,
      enabled: !profile.enabled,
    }, {
      onSuccess: () => toast.success(profile.enabled
        ? t('egress_profiles.toast.disabled')
        : t('egress_profiles.toast.enabled')),
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function remove(profile: EgressProfileRecord): void {
    deleteProfile.mutate(profile.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        toast.success(t('egress_profiles.toast.deleted'));
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  const rows = (profiles.data ?? []).filter((profile) => !isChannelInstanceProfile(profile));

  return (
    <section className="flex flex-col gap-6" data-testid="egress-profiles-page">
      <PageHeader
        title={t('egress_profiles.title')}
        sub={t('egress_profiles.subtitle')}
        actions={
          <div
            className="flex flex-wrap items-center gap-2 lg:justify-end"
            data-testid="egress-profiles-actions"
          >
            <Link to="/supply/upstreams" className={buttonClass('secondary')}>
              <Route aria-hidden="true" className="h-4 w-4" />
              <span>{t('egress_profiles.instance_note.cta')}</span>
            </Link>
            <Button
              variant="primary"
              onClick={() => setCreateOpen(true)}
              disabled={editingDisabled}
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              <span>{t('egress_profiles.create_btn')}</span>
            </Button>
          </div>
        }
      />

      <ProtocolSidecarStatusPanel
        status={protocolSidecar.data}
        loading={protocolSidecar.isLoading}
        error={protocolSidecar.error}
        editingDisabled={editingDisabled}
      />

      {profiles.isLoading ? (
        <div className="h-40 animate-pulse rounded-lg bg-well" />
      ) : profiles.isError ? (
        <p className="m-0 text-body text-bad" role="alert">
          {coerceErrorMessage(profiles.error)}
        </p>
      ) : (
        <Table className="min-w-0" testId="egress-profiles-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('egress_profiles.col.name')}</Th>
              <Th>{t('egress_profiles.col.type')}</Th>
              <Th>{t('egress_profiles.col.strategy')}</Th>
              <Th>{t('egress_profiles.col.usage')}</Th>
              <Th>{t('egress_profiles.col.status')}</Th>
              <Th>{t('egress_profiles.col.updated')}</Th>
              <Th className="text-right">{t('egress_profiles.col.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={7}>{t('egress_profiles.empty')}</TableEmpty>
            ) : rows.map((profile) => {
              const deleteBlocked = profile.usage.key_channel_bindings.length > 0;
              return (
                <Tr key={profile.id}>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <span className="text-dense font-medium text-paper">{profile.name}</span>
                      <span className="font-mono text-label text-paper-low">{profile.id}</span>
                    </div>
                  </Td>
                  <Td>
                    <Chip tone="signal" lamp={false} className="font-mono">
                      {profileLaneLabel(profile, t)}
                    </Chip>
                  </Td>
                  <Td className="max-w-[420px]">
                    {profile.egress_config === null ? (
                      <span className="text-dense text-paper-low">
                        {t('egress_profiles.strategy.legacy_hint')}
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {profileStrategyAxes(profile.egress_config).map((axis) => (
                          <span
                            key={`${axis.label}:${axis.value}`}
                            className="inline-flex min-w-0 max-w-full items-center gap-1 rounded bg-raised px-2 py-1 text-micro leading-none text-paper-low"
                          >
                            <span className="shrink-0 font-medium">{axis.label}</span>
                            <span className="min-w-0 truncate font-mono text-paper-mid">{axis.value}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </Td>
                  <Td className="max-w-[280px]">
                    <UsageCell usage={profile.usage} />
                  </Td>
                  <Td>
                    <Chip tone={profile.enabled ? 'ok' : 'neutral'}>
                      {profile.enabled
                        ? t('egress_profiles.status.enabled')
                        : t('egress_profiles.status.disabled')}
                    </Chip>
                  </Td>
                  <Td className="whitespace-nowrap font-mono text-label text-paper-low">
                    {formatLocalTimestamp(profile.updated_at, { seconds: true })}
                  </Td>
                  <Td className="whitespace-nowrap text-right">
                    <span className="inline-flex items-center justify-end gap-1">
                      <IconButton
                        size="sm"
                        label={t('egress_profiles.action.edit')}
                        onClick={() => openEdit(profile)}
                        disabled={updateProfile.isPending}
                      >
                        <FilePen aria-hidden="true" className="h-4 w-4" />
                      </IconButton>
                      <IconButton
                        size="sm"
                        label={profile.enabled
                          ? t('egress_profiles.action.disable')
                          : t('egress_profiles.action.enable')}
                        onClick={() => toggle(profile)}
                        disabled={updateProfile.isPending}
                      >
                        {profile.enabled ? (
                          <CirclePause aria-hidden="true" className="h-4 w-4" />
                        ) : (
                          <CirclePlay aria-hidden="true" className="h-4 w-4" />
                        )}
                      </IconButton>
                      <IconButton
                        size="sm"
                        label={deleteBlocked
                          ? t('egress_profiles.action.delete_blocked')
                          : t('egress_profiles.action.delete')}
                        onClick={() => setDeleteTarget(profile)}
                        disabled={deleteProfile.isPending || deleteBlocked}
                        className="hover:bg-bad-dim hover:text-bad"
                      >
                        <Trash2 aria-hidden="true" className="h-4 w-4" />
                      </IconButton>
                    </span>
                  </Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}

      {createOpen ? (
        <ProfileModal
          key="create"
          title={t('egress_profiles.modal.create_title')}
          description={t('egress_profiles.modal.create_hint')}
          name={name}
          initialConfig={null}
          wireApi={wireApi}
          capabilities={capabilities}
          pending={createProfile.isPending}
          configValid={config !== null}
          submitLabel={t('egress_profiles.modal.create_submit')}
          onNameChange={setName}
          onConfigChange={setConfig}
          onWireApiChange={setWireApi}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
        />
      ) : null}

      {editingProfile !== null ? (
        <ProfileModal
          key={editingProfile.id}
          title={t('egress_profiles.modal.edit_title')}
          description={editingProfile.usage.key_channel_bindings.length > 0
            ? t('egress_profiles.modal.save_warning_count', {
              count: editingProfile.usage.key_channel_bindings.length,
            })
            : t('egress_profiles.modal.save_warning_none')}
          name={editName}
          initialConfig={editingProfile.egress_config}
          wireApi={editWireApi}
          capabilities={capabilities}
          pending={updateProfile.isPending}
          configValid={editConfig !== null}
          submitLabel={t('egress_profiles.modal.edit_submit')}
          onNameChange={setEditName}
          onConfigChange={setEditConfig}
          onWireApiChange={setEditWireApi}
          onClose={() => setEditingProfile(null)}
          onSubmit={handleEdit}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next && !deleteProfile.isPending) setDeleteTarget(null);
        }}
        title={t('egress_profiles.delete.title')}
        body={deleteTarget === null
          ? undefined
          : t('egress_profiles.delete.body', { name: deleteTarget.name })}
        tone="danger"
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={deleteProfile.isPending}
        onConfirm={() => {
          if (deleteTarget !== null) remove(deleteTarget);
        }}
        testId="egress-profile-delete-confirm"
      />
    </section>
  );
}

function ProtocolSidecarStatusPanel({
  status,
  loading,
  error,
  editingDisabled,
}: {
  readonly status: InternalProtocolSidecarStatusResponse | undefined;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly editingDisabled: boolean;
}): JSX.Element {
  const t = useT();
  const ready = status?.status === 'ready';
  const health = status?.health ?? null;
  const capabilities = status?.capabilities ?? null;
  const apiVersion = health?.apiVersion ?? capabilities?.apiVersion ?? 'unknown';
  const configHash = health?.configHash ?? 'unavailable';
  const sdkVersion = health?.sdkVersion ?? capabilities?.sdkVersion ?? 'unavailable';
  const detail = error !== null
    ? error.message
    : status?.error?.message ?? (ready ? t('egress_profiles.sidecar.ready_hint') : t('egress_profiles.sidecar.degraded_hint'));
  return (
    <section
      className="rounded-lg border border-line-soft bg-card p-4"
      data-testid="protocol-sidecar-status"
      aria-label={t('egress_profiles.sidecar.title')}
    >
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden="true"
            className={ready
              ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-raised text-ok'
              : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warn-dim text-warn'}
          >
            <Network aria-hidden="true" className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-section text-paper">
                {t('egress_profiles.sidecar.title')}
              </h2>
              <Chip tone={ready ? 'ok' : 'warn'} pulse={ready}>
                {loading ? t('common.loading') : ready ? t('egress_profiles.sidecar.ready') : t('egress_profiles.sidecar.degraded')}
              </Chip>
            </div>
            <p className="mt-1 max-w-3xl text-dense text-paper-low">
              {detail}
            </p>
            {!loading && editingDisabled ? (
              <p className="mt-1 max-w-3xl text-dense font-medium text-warn" data-testid="egress-editing-disabled">
                {t('egress_profiles.sidecar.editing_disabled')}
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          <HeaderChip icon={Package} label="SDK" value={sdkVersion} />
          <HeaderChip icon={Webhook} label={t('egress_profiles.sidecar.api')} value={apiVersion} />
          <HeaderChip icon={Fingerprint} label={t('egress_profiles.sidecar.config_hash')} value={configHash} />
        </div>
      </div>
    </section>
  );
}

function ProfileModal({
  title,
  description,
  name,
  initialConfig,
  wireApi,
  capabilities,
  pending,
  configValid,
  submitLabel,
  onNameChange,
  onConfigChange,
  onWireApiChange,
  onClose,
  onSubmit,
}: {
  readonly title: string;
  readonly description: string;
  readonly name: string;
  readonly initialConfig: EgressConfig | null;
  readonly wireApi: OpenAIWireApi;
  readonly capabilities: InternalProtocolSidecarCapabilities | null;
  readonly pending: boolean;
  readonly configValid: boolean;
  readonly submitLabel: string;
  readonly onNameChange: (value: string) => void;
  readonly onConfigChange: (value: EgressConfig | null) => void;
  readonly onWireApiChange: (value: OpenAIWireApi) => void;
  readonly onClose: () => void;
  readonly onSubmit: () => void;
}): JSX.Element {
  const t = useT();
  const editingDisabled = capabilities === null;
  // Fetched here rather than in the page so the list is only requested when a
  // modal is actually open, and only by the surface that offers the selector.
  const firewallRulesets = useFirewallRulesets();
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
      size="xl"
      title={title}
      description={description}
      closeLabel={t('common.close')}
      testId="egress-profile-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            loading={pending}
            disabled={pending || editingDisabled || !configValid || name.trim().length === 0}
          >
            <Save aria-hidden="true" className="h-4 w-4" />
            <span>{submitLabel}</span>
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label={t('egress_profiles.modal.name')} fieldId="egress-profile-name" required>
          <Input
            id="egress-profile-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            disabled={pending || editingDisabled}
            required
          />
        </FormField>
        <FormField label={t('egress_profiles.modal.wire_api')} fieldId="egress-profile-wire-api">
          <Select
            id="egress-profile-wire-api"
            value={wireApi}
            onValueChange={(value) => {
              if (isOneOf(value, KNOWN_OPENAI_WIRE_APIS)) {
                onWireApiChange(value);
              }
            }}
            disabled={pending || editingDisabled}
            ariaLabel={t('egress_profiles.modal.wire_api')}
            testId="egress-profile-wire-api"
            options={[
              { value: 'both', label: t('egress_profiles.modal.wire_api.both') },
              { value: 'chat_completions', label: t('egress_profiles.modal.wire_api.chat_completions') },
              { value: 'responses', label: t('egress_profiles.modal.wire_api.responses') },
            ]}
          />
        </FormField>
        <EgressConfigEditor
          value={initialConfig}
          capabilities={capabilities}
          disabled={pending}
          idPrefix="profile-modal"
          firewallRulesets={firewallRulesets.data ?? []}
          firewallRulesetsUnavailable={firewallRulesets.isError}
          firewallScope="profile"
          onChange={onConfigChange}
        />
      </div>
    </Dialog>
  );
}

function HeaderChip({
  icon: IconComponent,
  label,
  value,
}: {
  readonly icon: typeof Package;
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <span className="inline-flex min-w-0 items-center gap-2 rounded-lg bg-well px-3 py-2">
      <IconComponent aria-hidden="true" className="h-4 w-4 shrink-0 text-paper-low" />
      <span className="shrink-0 text-label font-medium text-paper-low">{label}</span>
      <span className="min-w-0 truncate font-mono text-label text-paper-mid">{value}</span>
    </span>
  );
}
